import type { CmsProduct } from '../../types/cmsProduct';
import type { EligibilityOutcome } from '../../types/eligibility';

/**
 * The eligibility outcome a product event reports as `eo` inside
 * `product_context_parameter` (CMS-1905 Analytics 3, specified under
 * CMS-2063).
 *
 * ⚠️ **Module state, on purpose.** The outcome lives in
 * `ProductEligibilityProvider`, but `sendProductAnalytics` is a plain function
 * with some fifty callers across this package and fe-services, most of which
 * have no reason to know the eligibility check exists. The spec puts `eo` on
 * every event that carries the bag on a page offering the check, so the
 * provider registers its resolver here and the one function that builds the
 * bag asks it. Registration happens in an effect, so the server never holds
 * one, and a page without the provider — every other vertical, a standalone
 * product page — never gets `eo` at all (CMS-2063, 2026-10-08).
 */
type EligibilityOutcomeResolver = (product: CmsProduct) => EligibilityOutcome;

let resolver: EligibilityOutcomeResolver | undefined;

/** Registers the page's resolver; the returned function unregisters it. */
export const registerEligibilityOutcomeResolver = (
  next: EligibilityOutcomeResolver,
): (() => void) => {
  resolver = next;
  return () => {
    // Only its own: a provider remounting registers before the old one's
    // cleanup runs.
    if (resolver === next) resolver = undefined;
  };
};

/** `undefined` when the page offers no eligibility check. */
export const eligibilityOutcomeFor = (
  product: CmsProduct,
): EligibilityOutcome | undefined => resolver?.(product);

/** A total map, so a new outcome does not compile until it has a value. */
const EO_VALUES: Record<EligibilityOutcome, string> = {
  MEETS: 'eligible',
  // Income takes precedence when both fall short (AC 3d), and the spec reports
  // that case as income too.
  SHORT_INCOME: 'ineligible_income',
  SHORT_CITIZENSHIP: 'ineligible_citizenship',
  // No income entered, or a lender publishing no criteria: the default.
  NONE: 'unavailable',
};

export const eligibilityContextPart = (outcome: EligibilityOutcome): string =>
  `eo-${EO_VALUES[outcome]}`;
